import React, { useCallback, useEffect, useState } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { API_ORIGIN } from '../../../config/api';

const FIELD_FRAGMENT = `
  id
  name
  active
  defaultServiceId
  createdAt
  notifyEmails
  fields { name label type required options }
`;

const GET_LEAD_FORMS = `query GetLeadForms { getLeadForms { ${FIELD_FRAGMENT} } }`;

const GET_SERVICES = `
  query GetService { getService(includeStats: false) { id service } }
`;

const CREATE_LEAD_FORM = `
  mutation CreateLeadForm($input: CreateLeadFormInput!) {
    createLeadForm(input: $input) { ${FIELD_FRAGMENT} }
  }
`;

const SET_FIELDS = `
  mutation SetLeadFormFields($id: String!, $fields: [LeadFormFieldInput!]!) {
    setLeadFormFields(id: $id, fields: $fields) { ${FIELD_FRAGMENT} }
  }
`;

const SET_ACTIVE = `
  mutation SetLeadFormActive($id: String!, $active: Boolean!) {
    setLeadFormActive(id: $id, active: $active) { id active }
  }
`;

const REMOVE_LEAD_FORM = `
  mutation RemoveLeadForm($id: String!) { removeLeadForm(id: $id) }
`;

const SET_NOTIFY_EMAILS = `
  mutation SetLeadFormNotifyEmails($id: String!, $emails: [String!]!) {
    setLeadFormNotifyEmails(id: $id, emails: $emails) { ${FIELD_FRAGMENT} }
  }
`;

const parseEmails = (text) =>
  (text || '')
    .split(/[\s,;\n]+/)
    .map((e) => e.trim())
    .filter(Boolean);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const validEmails = (list) => list.every((e) => EMAIL_RE.test(e));

const FIELD_TYPES = ['text', 'email', 'tel', 'textarea', 'select', 'date', 'number'];

const slug = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);

const blankField = () => ({
  label: '',
  name: '',
  type: 'text',
  required: false,
  optionsText: '',
});

// Strip UI-only keys, derive name from label if missing, build API shape.
const toApiFields = (rows) =>
  rows
    .filter((r) => r.label.trim())
    .map((r) => ({
      label: r.label.trim(),
      name: (r.name || slug(r.label)).trim(),
      type: r.type,
      required: !!r.required,
      options:
        r.type === 'select'
          ? (r.optionsText || '')
              .split(',')
              .map((o) => o.trim())
              .filter(Boolean)
          : null,
    }));

const fromApiFields = (fields) =>
  (fields || []).map((f) => ({
    label: f.label,
    name: f.name,
    type: f.type,
    required: f.required,
    optionsText: (f.options || []).join(', '),
  }));

const submitFormUrl = (id) => `${API_ORIGIN}/embed/forms/${id}/submit-form`;
const publicFormUrl = (id) => `${API_ORIGIN}/embed/forms/${id}/page`;

const hostedFormUrl = (id, source = 'Website', campaign = '') => {
  const params = new URLSearchParams({ layout: 'sas', source });
  if (String(campaign || '').trim()) {
    params.set('campaign', String(campaign).trim());
  }
  return `${API_ORIGIN}/embed/forms/${id}/page?${params.toString()}`;
};

// Recommended for website builders that strip scripts / hijack <form>s.
// layout=sas + transparent=1: matches SAS-style 2-column form inside your white card
const iframeEmbed = (id) =>
  `<iframe src="${API_ORIGIN}/embed/forms/${id}/page?layout=sas&transparent=1" title="Contact form" style="width:100%;min-height:920px;border:0;background:transparent;display:block" loading="lazy"></iframe>`;

const reactSnippet = (id) =>
  `<iframe
  src="${API_ORIGIN}/embed/forms/${id}/page?layout=sas&transparent=1"
  title="Contact form"
  style={{ width: '100%', minHeight: 920, border: 0, background: 'transparent', display: 'block' }}
  loading="lazy"
/>`;

const vueSnippet = (id) =>
  `<iframe
  src="${API_ORIGIN}/embed/forms/${id}/page?layout=sas&transparent=1"
  title="Contact form"
  style="width:100%;min-height:920px;border:0;background:transparent;display:block"
  loading="lazy"
></iframe>`;

const angularSnippet = (id) =>
  `<!-- In your component template. You need a SafeUrl pipe (DomSanitizer) for [src]. -->
<iframe
  [src]="'${API_ORIGIN}/embed/forms/${id}/page?layout=sas&transparent=1' | safeUrl"
  title="Contact form"
  style="width:100%;min-height:920px;border:0;background:transparent;display:block"
  loading="lazy"
></iframe>`;

const apiUrl = (id) => `${API_ORIGIN}/embed/forms/${id}/submit`;
const webhookUrl = (id) => `${API_ORIGIN}/embed/forms/${id}/webhook`;

const fetchSnippet = (id) =>
  `// POST JSON to your form's submit endpoint from any frontend
// (React, Vue, vanilla JS, React Native, etc.). No auth, no iframe.
const submitLead = async (data) => {
  const res = await fetch('${apiUrl(id)}', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fullName: data.fullName,
      email: data.email,
      phone: data.phone,
      address: data.address,
      // optional:
      // postcode, service, bookingFrequency, message, preferredDate
    }),
  });
  if (!res.ok) throw new Error('Submission failed');
  return res.json();
};`;

const axiosSnippet = (id) =>
  `// npm i axios   (or yarn add axios)
import axios from 'axios';

await axios.post('${apiUrl(id)}', {
  fullName: 'Jane Doe',
  email: 'jane@example.com',
  phone: '0412 345 678',
  address: '123 Main St',
  // optional: postcode, service, bookingFrequency, message, preferredDate
});`;

const curlSnippet = (id) =>
  `# Test from terminal, or call from any backend (PHP cURL, Go net/http,
# .NET HttpClient, Python requests, etc.) — no auth required.
curl -X POST '${apiUrl(id)}' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "fullName": "Jane Doe",
    "email": "jane@example.com",
    "phone": "0412 345 678",
    "address": "123 Main St"
  }'`;

const STACKS = [
  {
    id: 'html',
    label: 'Plain HTML / Static site',
    instructions: 'Paste anywhere inside the <body> of your .html file. Works as-is.',
    snippet: (id) => iframeEmbed(id),
  },
  {
    id: 'wordpress',
    label: 'WordPress',
    instructions:
      'Block editor: add a "Custom HTML" block and paste. Classic editor: switch to the "Text" tab and paste. Avoid the "Visual" tab — it will mangle the HTML.',
    snippet: (id) => iframeEmbed(id),
  },
  {
    id: 'wix',
    label: 'Wix',
    instructions:
      'Add → Embed Code → Embed HTML → paste → Update. IMPORTANT: the editor preview shows a placeholder, not the form. You must click PUBLISH and view the live site to see it.',
    snippet: (id) => iframeEmbed(id),
  },
  {
    id: 'squarespace',
    label: 'Squarespace',
    instructions:
      'Add a "Code" block, set type to HTML, paste this, and make sure "Display Source" is OFF. Publish the page to see it live.',
    snippet: (id) => iframeEmbed(id),
  },
  {
    id: 'webflow',
    label: 'Webflow',
    instructions:
      'Drag an "Embed" component (HTML embed) onto the page and paste this. The designer shows a placeholder — Publish to staging/production to see the actual form.',
    snippet: (id) => iframeEmbed(id),
  },
  {
    id: 'shopify',
    label: 'Shopify',
    instructions:
      'Page editor: Add section → "Custom Liquid" → paste this. Or paste into a theme .liquid file. Save and refresh the storefront.',
    snippet: (id) => iframeEmbed(id),
  },
  {
    id: 'godaddy',
    label: 'GoDaddy / Hostinger / Other Builder',
    instructions:
      'Add an "HTML" / "Embed" / "Custom Code" section and paste. Save and publish. If the editor preview is blank, view the published site — many builders sandbox iframes in preview mode.',
    snippet: (id) => iframeEmbed(id),
  },
  {
    id: 'react',
    label: 'React / Next.js',
    instructions: 'Paste this JSX inside your component\'s return.',
    snippet: (id) => reactSnippet(id),
  },
  {
    id: 'vue',
    label: 'Vue / Nuxt',
    instructions: 'Paste inside <template>. Self-closing iframe is not valid in HTML, keep the closing tag.',
    snippet: (id) => vueSnippet(id),
  },
  {
    id: 'angular',
    label: 'Angular',
    instructions:
      'Angular blocks unsanitized iframe URLs. Bypass with DomSanitizer (SafeUrl pipe shown in the snippet).',
    snippet: (id) => angularSnippet(id),
  },
  {
    id: 'rawhtml',
    label: 'Raw HTML form (use if iframe is blocked)',
    instructions:
      'Use this when the host site strips iframes or you want full visual control. It is a native <form> POST — no JS, no iframe. After submit, the browser navigates to a thank-you page served by your backend.',
    snippet: (id, form) => buildHtmlForm(form),
  },
  {
    id: 'js-fetch',
    label: 'JavaScript API (fetch) — build your own UI',
    instructions:
      'For developers who want full control: skip the iframe and POST JSON directly to the form endpoint. Works in React, Vue, vanilla JS, React Native — anywhere window.fetch exists. No auth, CORS open.',
    snippet: (id) => fetchSnippet(id),
  },
  {
    id: 'axios',
    label: 'API with Axios',
    instructions: 'Same JSON endpoint as fetch, but using axios. Drop into any React/Vue/Node project that already has axios.',
    snippet: (id) => axiosSnippet(id),
  },
  {
    id: 'curl',
    label: 'cURL / Server-to-Server',
    instructions:
      'Test the endpoint from a terminal, or call from any backend language (PHP, Go, .NET, Python, Ruby, Java). No auth required — perfect for server-side integrations where you do not want to expose forms client-side.',
    snippet: (id) => curlSnippet(id),
  },
];

// Ready-to-paste HTML form reflecting the configured fields (no JS needed).
const buildHtmlForm = (form) => {
  const inputCss =
    'width:100%;padding:12px;border:1px solid #d6e2ec;border-radius:10px;font-size:15px';
  const core = [
    ['Full Name *', '<input type="text" name="full_name" required>'],
    ['Phone *', '<input type="tel" name="phone" required>'],
    ['Email *', '<input type="email" name="email" required>'],
    ['Suburb / Address *', '<input type="text" name="address" required>'],
  ];

  const custom = (form.fields || []).map((f) => {
    const req = f.required ? ' required' : '';
    let control;
    if (f.type === 'textarea') {
      control = `<textarea name="${f.name}"${req} rows="4" style="${inputCss}"></textarea>`;
    } else if (f.type === 'select') {
      const opts = (f.options || [])
        .map((o) => `<option>${o}</option>`)
        .join('');
      control = `<select name="${f.name}"${req} style="${inputCss}"><option value="">Select</option>${opts}</select>`;
    } else {
      control = `<input type="${f.type}" name="${f.name}"${req} style="${inputCss}">`;
    }
    return [`${f.label}${f.required ? ' *' : ''}`, control];
  });

  const rows = [...core, ...custom]
    .map(
      ([label, ctrl]) =>
        `  <label style="display:block;font-weight:700;margin:14px 0 6px">${label}</label>\n  ${ctrl.includes('style=') ? ctrl : ctrl.replace('>', ` style="${inputCss}">`)}`,
    )
    .join('\n');

  return `<form action="${submitFormUrl(form.id)}" method="POST" style="max-width:560px;font-family:Arial,sans-serif">
  <input type="text" name="website" style="display:none" tabindex="-1" autocomplete="off">
${rows}
  <label style="display:block;font-weight:700;margin:14px 0 6px">Message</label>
  <textarea name="message" rows="5" style="${inputCss}"></textarea>
  <button type="submit" style="margin-top:18px;width:100%;padding:14px;border:0;border-radius:10px;background:#063b66;color:#fff;font-size:16px;font-weight:800;cursor:pointer">Submit</button>
</form>`;
};

const FieldRows = ({ rows, setRows }) => {
  const update = (i, patch) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <div>
      {rows.map((r, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: 8,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            padding: 8,
          }}
        >
          <input
            placeholder="Label (e.g. Property Size)"
            value={r.label}
            onChange={(e) =>
              update(i, {
                label: e.target.value,
                name: r.name || slug(e.target.value),
              })
            }
            style={{ flex: '1 1 160px', padding: 7, border: '1px solid #d1d5db', borderRadius: 4 }}
          />
          <input
            placeholder="field_name"
            value={r.name}
            onChange={(e) => update(i, { name: slug(e.target.value) })}
            style={{ flex: '1 1 130px', padding: 7, border: '1px solid #d1d5db', borderRadius: 4 }}
          />
          <select
            value={r.type}
            onChange={(e) => update(i, { type: e.target.value })}
            style={{ padding: 7, border: '1px solid #d1d5db', borderRadius: 4 }}
          >
            {FIELD_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {r.type === 'select' && (
            <input
              placeholder="comma,separated,options"
              value={r.optionsText}
              onChange={(e) => update(i, { optionsText: e.target.value })}
              style={{ flex: '1 1 180px', padding: 7, border: '1px solid #d1d5db', borderRadius: 4 }}
            />
          )}
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="checkbox"
              checked={r.required}
              onChange={(e) => update(i, { required: e.target.checked })}
            />
            required
          </label>
          <button
            type="button"
            onClick={() => remove(i)}
            style={{ border: 0, background: '#ef4444', color: '#fff', borderRadius: 4, padding: '6px 10px', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setRows((prev) => [...prev, blankField()])}
        style={{ border: '1px dashed #9ca3af', background: '#fff', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}
      >
        + Add Field
      </button>
    </div>
  );
};

const EmbedForm = ({ onClose }) => {
  const [forms, setForms] = useState([]);
  const [services, setServices] = useState([]);
  const [name, setName] = useState('');
  const [defaultServiceId, setDefaultServiceId] = useState('');
  const [draftFields, setDraftFields] = useState([]);
  const [draftNotifyEmails, setDraftNotifyEmails] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editRows, setEditRows] = useState([]);
  const [notifyEditId, setNotifyEditId] = useState(null);
  const [notifyEditText, setNotifyEditText] = useState('');
  const [stackChoice, setStackChoice] = useState({});
  const [linkTracking, setLinkTracking] = useState({});

  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      const data = await graphqlRequest(GET_LEAD_FORMS);
      setForms(data?.getLeadForms || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadForms();
    (async () => {
      try {
        const data = await graphqlRequest(GET_SERVICES);
        setServices(data?.getService || []);
      } catch (err) {
        console.error('Error loading services:', err);
      }
    })();
  }, [loadForms]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Enter a form name');
      return;
    }
    setCreating(true);
    try {
      const input = { name: name.trim() };
      if (defaultServiceId) input.defaultServiceId = defaultServiceId;
      const apiFields = toApiFields(draftFields);
      if (apiFields.length) input.fields = apiFields;
      const notifyList = parseEmails(draftNotifyEmails);
      if (notifyList.length) {
        if (!validEmails(notifyList)) {
          toast.error('One or more notify emails are invalid');
          setCreating(false);
          return;
        }
        input.notifyEmails = notifyList;
      }
      const data = await graphqlRequest(CREATE_LEAD_FORM, { input });
      const created = data?.createLeadForm;
      if (created) {
        setForms((prev) => [created, ...prev]);
        setName('');
        setDefaultServiceId('');
        setDraftFields([]);
        setDraftNotifyEmails('');
        toast.success('Form generated');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to generate form');
    } finally {
      setCreating(false);
    }
  };

  const saveNotifyEmails = async (id) => {
    const list = parseEmails(notifyEditText);
    if (list.length && !validEmails(list)) {
      toast.error('One or more emails are invalid');
      return;
    }
    try {
      const data = await graphqlRequest(SET_NOTIFY_EMAILS, { id, emails: list });
      const updated = data?.setLeadFormNotifyEmails;
      if (updated) {
        setForms((prev) => prev.map((f) => (f.id === id ? updated : f)));
        setNotifyEditId(null);
        setNotifyEditText('');
        toast.success('Notify emails updated');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update notify emails');
    }
  };

  const saveFields = async (id) => {
    try {
      const fields = toApiFields(editRows);
      const data = await graphqlRequest(SET_FIELDS, { id, fields });
      const updated = data?.setLeadFormFields;
      if (updated) {
        setForms((prev) => prev.map((f) => (f.id === id ? updated : f)));
        setEditId(null);
        toast.success('Fields updated');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update fields');
    }
  };

  const toggleActive = async (form) => {
    try {
      await graphqlRequest(SET_ACTIVE, { id: form.id, active: !form.active });
      setForms((prev) =>
        prev.map((f) => (f.id === form.id ? { ...f, active: !form.active } : f)),
      );
    } catch (err) {
      toast.error(err.message || 'Failed to update form');
    }
  };

  const handleDelete = async (id) => {
    if (!await appConfirmMessage('Delete this form? Existing embeds will stop working.')) return;
    try {
      await graphqlRequest(REMOVE_LEAD_FORM, { id });
      setForms((prev) => prev.filter((f) => f.id !== id));
      toast.success('Form deleted');
    } catch (err) {
      toast.error(err.message || 'Failed to delete form');
    }
  };

  const copy = (text, label) =>
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error('Copy failed'));

  const box = { border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 12 };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        // Above NavBar (z-index 1100–2000) so the header + Cancel stay visible.
        zIndex: 10000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '24px 16px',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          background: '#fff',
          width: '92%',
          maxWidth: 860,
          maxHeight: 'calc(100vh - 48px)',
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          margin: '0 auto',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0,
            padding: '16px 24px',
            borderBottom: '1px solid #e5e7eb',
            background: 'var(--brand-primary, #5b5ce2)',
            color: '#fff',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 20, color: '#fff' }}>
            Lead Capture & Integrations
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: '1px solid rgba(255,255,255,0.55)',
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              borderRadius: 6,
              padding: '8px 16px',
              cursor: 'pointer',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            Cancel
          </button>
        </div>

        <div style={{ padding: 24, overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <div
          style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: 8,
            padding: '12px 14px',
            marginBottom: 16,
            fontSize: 13,
            lineHeight: 1.6,
            color: '#1e3a5f',
          }}
        >
          <strong>Choose how customers will send enquiries.</strong>
          Share a public link in social posts or advertisements, copy a website
          embed for your website platform, or open the advanced integration
          section for Google Lead Ads and lead marketplaces. Every successful
          submission is created as a lead inside Lead Management.
        </div>

        <form onSubmit={handleCreate} style={{ background: '#f9fafb', padding: 16, borderRadius: 8, marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Form Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Website Contact Form"
                style={{ width: '100%', marginTop: 4, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
              />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Default Service (optional)</label>
              <select
                value={defaultServiceId}
                onChange={(e) => setDefaultServiceId(e.target.value)}
                style={{ width: '100%', marginTop: 4, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
              >
                <option value="">None</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.service}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={creating}
              style={{ border: 0, background: '#4f46e5', color: '#fff', borderRadius: 6, padding: '9px 18px', cursor: 'pointer', opacity: creating ? 0.6 : 1 }}
            >
              {creating ? 'Creating...' : 'Create enquiry form'}
            </button>
          </div>

          <p style={{ fontSize: 13, fontWeight: 600, margin: '16px 0 6px' }}>
            Custom Fields (optional) — name, email, phone, address are always
            included. Add any extra fields you want captured:
          </p>
          <FieldRows rows={draftFields} setRows={setDraftFields} />

          <p style={{ fontSize: 13, fontWeight: 600, margin: '16px 0 6px' }}>
            Notify these emails on new submissions (optional)
          </p>
          <textarea
            value={draftNotifyEmails}
            onChange={(e) => setDraftNotifyEmails(e.target.value)}
            placeholder="sales@example.com, leads@example.com"
            rows={2}
            style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical' }}
          />
          <small style={{ color: '#6b7280', fontSize: 12 }}>
            Customer and your company email are always notified. Separate extras with commas, spaces or new lines.
          </small>
        </form>

        {loading ? (
          <p>Loading forms...</p>
        ) : forms.length === 0 ? (
          <p style={{ color: '#6b7280' }}>
            No enquiry forms yet. Create one above, then copy its share link or website embed.
          </p>
        ) : (
          forms.map((form) => (
            <div key={form.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <strong>{form.name}</strong>
                <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, background: form.active ? '#dcfce7' : '#fee2e2', color: form.active ? '#166534' : '#991b1b' }}>
                  {form.active ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div style={{ fontSize: 13, marginBottom: 6 }}>
                <span style={{ color: '#6b7280' }}>Form ID: </span>
                <code>{form.id}</code>
                <button onClick={() => copy(form.id, 'Form ID')} style={{ ...box, marginLeft: 8 }}>
                  Copy ID
                </button>
              </div>

              {(() => {
                const tracking = linkTracking[form.id] || {
                  source: 'Website',
                  campaign: slug(form.name).replace(/_/g, '-'),
                };
                const publicUrl = hostedFormUrl(
                  form.id,
                  tracking.source,
                  tracking.campaign,
                );
                const updateTracking = (patch) =>
                  setLinkTracking((prev) => ({
                    ...prev,
                    [form.id]: { ...tracking, ...patch },
                  }));
                return (
                  <div className="lead-form-share-panel">
                    <div className="lead-form-share-title">Shareable quote / enquiry link</div>
                    <div className="lead-form-share-controls">
                      <label>
                        <span>Lead source</span>
                        <select
                          value={tracking.source}
                          onChange={(e) => updateTracking({ source: e.target.value })}
                        >
                          <option>Website</option>
                          <option>Facebook</option>
                          <option>Google Ads</option>
                          <option>LinkedIn</option>
                          <option>Referral</option>
                          <option>Email</option>
                          <option>Other</option>
                        </select>
                      </label>
                      <label>
                        <span>Campaign</span>
                        <input
                          value={tracking.campaign}
                          onChange={(e) => updateTracking({ campaign: e.target.value })}
                          placeholder="e.g. perth-commercial-cleaning"
                        />
                      </label>
                    </div>
                    <div className="lead-form-share-link">
                      <code>{publicUrl}</code>
                      <button
                        type="button"
                        onClick={() => copy(publicUrl, 'Public form link')}
                        style={box}
                      >
                        Copy Link
                      </button>
                      <button
                        type="button"
                        onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
                        style={box}
                      >
                        Preview
                      </button>
                    </div>
                    <small>
                      Submissions retain this source and campaign in Lead Management.
                    </small>
                  </div>
                );
              })()}

              <details style={{ marginBottom: 10 }}>
                <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#334155' }}>
                  Advertising & marketplace integrations (advanced)
                </summary>
                <p style={{ margin: '8px 0', fontSize: 12, color: '#64748b' }}>
                  Use the webhook URL for Google Lead Ads, Cleynce, Bark, hipages,
                  Service.com.au, ServiceSeeking or another platform that can send
                  lead data by HTTP POST.
                </p>
              <div style={{ fontSize: 13, marginBottom: 6 }}>
                <span style={{ color: '#6b7280' }}>HTML form submission URL: </span>
                <code style={{ wordBreak: 'break-all' }}>{submitFormUrl(form.id)}</code>
                <button
                  onClick={() => copy(submitFormUrl(form.id), 'Form post URL')}
                  style={{ ...box, marginLeft: 8 }}
                >
                  Copy URL
                </button>
              </div>

              <div style={{ fontSize: 13, marginBottom: 8 }}>
                <span style={{ color: '#6b7280' }}>Webhook URL (POST only): </span>
                <code style={{ wordBreak: 'break-all' }}>{webhookUrl(form.id)}</code>
                <button
                  onClick={() => copy(webhookUrl(form.id), 'Webhook URL')}
                  style={{ ...box, marginLeft: 8 }}
                >
                  Copy URL
                </button>
              </div>
              </details>

              <div style={{ fontSize: 13, marginBottom: 8 }}>
                <span style={{ color: '#6b7280' }}>Fields: </span>
                {form.fields && form.fields.length
                  ? form.fields
                      .map((f) => `${f.label}${f.required ? '*' : ''}`)
                      .join(', ')
                  : 'core only (name, email, phone, address)'}
              </div>

              <div style={{ fontSize: 13, marginBottom: 8 }}>
                <span style={{ color: '#6b7280' }}>Notify emails: </span>
                {form.notifyEmails && form.notifyEmails.length
                  ? form.notifyEmails.join(', ')
                  : 'none (only customer + company email)'}
              </div>

              {(() => {
                const stackId = stackChoice[form.id] || 'html';
                const stack = STACKS.find((s) => s.id === stackId) || STACKS[0];
                const snippet = stack.snippet(form.id, form);
                return (
                  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, marginBottom: 10 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Choose your website platform
                    </label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                      <select
                        value={stackId}
                        onChange={(e) => setStackChoice((prev) => ({ ...prev, [form.id]: e.target.value }))}
                        style={{ flex: '1 1 240px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
                      >
                        {STACKS.map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => copy(snippet, `${stack.label} snippet`)}
                        style={{ border: 0, background: '#0b7637', color: '#fff', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontSize: 13 }}
                      >
                        Copy website embed
                      </button>
                    </div>
                    <div style={{ fontSize: 12, color: '#475569', marginBottom: 6, lineHeight: 1.5 }}>
                      {stack.instructions}
                    </div>
                    <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: 10, borderRadius: 6, fontSize: 12, overflow: 'auto', maxHeight: 200, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {snippet}
                    </pre>
                  </div>
                );
              })()}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    setEditId(form.id);
                    setEditRows(fromApiFields(form.fields));
                  }}
                  style={{ ...box, padding: '6px 12px', fontSize: 13 }}
                >
                  Edit Fields
                </button>
                <button
                  onClick={() => {
                    setNotifyEditId(form.id);
                    setNotifyEditText((form.notifyEmails || []).join(', '));
                  }}
                  style={{ ...box, padding: '6px 12px', fontSize: 13 }}
                >
                  Edit Notify Emails
                </button>
                <button onClick={() => toggleActive(form)} style={{ ...box, padding: '6px 12px', fontSize: 13 }}>
                  {form.active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDelete(form.id)}
                  style={{ border: 0, background: '#ef4444', color: '#fff', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}
                >
                  Delete
                </button>
              </div>

              {editId === form.id && (
                <div style={{ marginTop: 12, background: '#f9fafb', padding: 12, borderRadius: 8 }}>
                  <FieldRows rows={editRows} setRows={setEditRows} />
                  <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => saveFields(form.id)}
                      style={{ border: 0, background: '#16a34a', color: '#fff', borderRadius: 6, padding: '7px 16px', cursor: 'pointer' }}
                    >
                      Save Fields
                    </button>
                    <button onClick={() => setEditId(null)} style={{ ...box, padding: '7px 16px' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {notifyEditId === form.id && (
                <div style={{ marginTop: 12, background: '#f9fafb', padding: 12, borderRadius: 8 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    Notify emails (comma or newline separated)
                  </label>
                  <textarea
                    value={notifyEditText}
                    onChange={(e) => setNotifyEditText(e.target.value)}
                    placeholder="sales@example.com, leads@example.com"
                    rows={3}
                    style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical' }}
                  />
                  <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => saveNotifyEmails(form.id)}
                      style={{ border: 0, background: '#16a34a', color: '#fff', borderRadius: 6, padding: '7px 16px', cursor: 'pointer' }}
                    >
                      Save Notify Emails
                    </button>
                    <button
                      onClick={() => {
                        setNotifyEditId(null);
                        setNotifyEditText('');
                      }}
                      style={{ ...box, padding: '7px 16px' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
        </div>
      </div>
    </div>
  );
};

export default EmbedForm;
